import { EditorState } from "@codemirror/state";

import { parser } from "components/organisms/manuscript/editor/latex/parser/latex.mjs";
import { COMMAND_SUBSTITUTIONS } from "components/organisms/manuscript/editor/visual/rendering/widgets/character";

const textFormattingMarkupMap = new Map([
    ["TextBoldCommand", { elementType: "b" }],
    ["TextItalicCommand", { elementType: "i" }],
    ["TextSmallCapsCommand", { elementType: "span", className: "ol-cm-command-textsc" }],
    ["TextTeletypeCommand", { elementType: "span", className: "ol-cm-command-texttt" }],
    ["TextSuperscriptCommand", { elementType: "sup" }],
    ["TextSubscriptCommand", { elementType: "sub" }],
    ["EmphasisCommand", { elementType: "em" }],
    ["UnderlineCommand", { elementType: "span", className: "ol-cm-command-underline" }],
]);

const markupMap = new Map([["\\and", { elementType: "span", className: "ol-cm-command-and" }]]);

function chooseFrom(node) {
    return node.getChild("TextArgument")?.getChild("LongArg")?.from ?? node.to;
}

function unknownCommandName(node, getText) {
    if (node.type.is("Command")) {
        const innerUnknown = node.getChild("UnknownCommand");
        if (innerUnknown) {
            return unknownCommandName(innerUnknown, getText);
        }
    }

    if (node.type.is("UnknownCommand")) {
        const commandNameNode = node.getChild("$CtrlSeq");
        if (commandNameNode) {
            return getText(commandNameNode.from, commandNameNode.to).trim();
        }
    }
}

function shouldHandleLeave(node, getText) {
    if (textFormattingMarkupMap.has(node.type.name)) {
        return true;
    }

    const commandName = unknownCommandName(node, getText);
    return commandName && markupMap.has(commandName);
}

export function typesetNodeIntoElement(node, element, getText) {
    if (!element || !node) {
        return element;
    }

    if (typeof node === "string") {
        const text = node;
        const tree = parser.parse(text);
        return typesetNodeIntoElement(tree.topNode, element, text.substring.bind(text));
    }

    if (getText instanceof EditorState) {
        getText = getText.sliceDoc.bind(getText);
    }

    if (typeof getText !== "function") {
        const text = String(node);
        const tree = parser.parse(text);
        return typesetNodeIntoElement(tree.topNode, element, text.substring.bind(text));
    }

    const argument = node.getChild("LongArg");
    if (argument) {
        node = argument;
    }

    const ancestorStack = [element];
    const ancestor = () => ancestorStack[ancestorStack.length - 1];
    const popAncestor = () => ancestorStack.pop();
    const pushAncestor = (nextElement) => ancestorStack.push(nextElement);

    let from = node.from;

    const addMarkup = (markup, childNode) => {
        const nextElement = document.createElement(markup.elementType);
        if (markup.className) {
            nextElement.classList.add(markup.className);
        }
        pushAncestor(nextElement);
        from = chooseFrom(childNode);
    };

    node.cursor().iterate(
        (childNodeRef) => {
            const childNode = childNodeRef.node;

            if (from < childNode.from) {
                ancestor().append(document.createTextNode(getText(from, childNode.from)));
                from = childNode.from;
            }

            const markup = textFormattingMarkupMap.get(childNode.type.name);
            if (markup) {
                addMarkup(markup, childNode);
                return;
            }

            const commandName = unknownCommandName(childNode, getText);
            if (commandName) {
                const commandMarkup = markupMap.get(commandName);
                if (commandMarkup) {
                    addMarkup(commandMarkup, childNode);
                    return;
                }

                if (["\\corref", "\\fnref", "\\thanks"].includes(commandName)) {
                    from = childNode.to;
                    return false;
                }

                const symbol = COMMAND_SUBSTITUTIONS.get(commandName);
                if (symbol) {
                    ancestor().append(document.createTextNode(symbol));
                    from = childNode.to;
                    return false;
                }
            } else if (childNode.type.is("LineBreak")) {
                ancestor().append(document.createElement("br"));
                from = childNode.to;
            }
        },
        (childNodeRef) => {
            const childNode = childNodeRef.node;

            if (shouldHandleLeave(childNode, getText)) {
                const typeSetElement = popAncestor();
                ancestor().appendChild(typeSetElement);
                const textArgument = childNode.getChild("TextArgument");
                const endBrace = textArgument?.getChild("CloseBrace");
                if (endBrace) {
                    from = endBrace.to;
                }
            }
        },
    );

    if (from < node.to) {
        ancestor().append(document.createTextNode(getText(from, node.to)));
    }

    return element;
}
